import { Link } from "react-router-dom";
import { Check, Flame, Lock } from "lucide-react";
import WorldScene from "@/components/WorldScene";

export default function Home({ state, onOpenReward }) {
  const w = state.current_week;
  const total = w.required_english_tasks + w.required_drum_sessions;
  const done = w.english_done + w.drum_done;
  const remaining = Math.max(0, total - done);
  const level = state.level;
  const pct = Math.min(100, Math.round(((level.current_xp - level.level_min_xp) / Math.max(1, level.level_max_xp - level.level_min_xp)) * 100));
  const unlocked = state.unlocks.owned_codes;
  const lockedReward = state.rewards.find((r) => r.status === "locked");

  // Mascot state: sleeping at night (>=20 or <7), celebrating when done, happy if partial, idle otherwise
  const hour = new Date().getHours();
  const isNight = hour >= 20 || hour < 7;
  const mascotState = isNight
    ? "sleeping"
    : total > 0 && done === total ? "celebrating" : done > 0 ? "happy" : "idle";

  const nodes = [
    ...w.english_quests.map((q, i) => ({
      key: `e${i}`, kind: "english", done: !!q.completed_at, href: `/english/${q.id}`,
    })),
    ...Array.from({ length: w.required_drum_sessions }).map((_, i) => ({
      key: `d${i}`, kind: "drum", done: i < w.drum_done, href: "/drum",
    })),
  ];

  // The right column (quests, drum, level, mystery) — shared between mobile stacked view
  // and desktop two-column view.
  const RightColumn = (
    <>
      {/* Weekly five-node path */}
      <section>
        <div className="flex items-end justify-between mb-3 px-1">
          <h2 className="text-[22px] text-[hsl(var(--ink))]">This week</h2>
          <span className="text-sm font-semibold text-[hsl(var(--ink-soft))]" data-testid="weekly-progress-count">
            {done} / {total}
          </span>
        </div>
        <div className="relative" data-testid="weekly-progress-nodes">
          <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-[hsl(var(--surface-2))]"/>
          <div
            className="absolute left-6 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-[hsl(var(--violet))] shadow-[0_6px_14px_-6px_hsla(258,45%,48%,0.6)] transition-all"
            style={{ width: `calc(${(done / Math.max(1, total)) * 100}% - 24px)` }}
          />
          <ol className="relative flex items-center justify-between gap-1">
            {nodes.map((n, i) => (
              <li key={n.key} className="flex flex-col items-center">
                <Link
                  to={n.href}
                  data-testid={`node-${n.key}`}
                  className={`relative w-12 h-12 rounded-full flex items-center justify-center transition
                    ${n.done
                      ? "bg-gradient-to-b from-[hsl(var(--violet))] to-[hsl(var(--violet-deep))] text-white shadow-[0_10px_20px_-8px_hsla(258,45%,48%,0.7)]"
                      : "bg-white text-[hsl(var(--ink))] shadow-[0_8px_18px_-10px_hsla(258,20%,20%,0.3),0_1px_0_rgba(255,255,255,0.9)_inset] hover:-translate-y-0.5"
                    }`}
                >
                  {n.done ? <Check className="w-5 h-5" strokeWidth={3}/> : <span className="text-[13px] font-semibold">{i + 1}</span>}
                  {!n.done && (
                    <span className={`absolute -bottom-1 right-1 w-3 h-3 rounded-full ${n.kind === "english" ? "bg-[hsl(var(--amber))]" : "bg-[hsl(var(--violet))]"}`}/>
                  )}
                </Link>
                <span className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--ink-mute))]">
                  {n.kind === "english" ? "ENG" : "DRUM"}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* English + Drum cards */}
      <section className="space-y-3">
        {w.english_quests.map((q) => {
          const done = !!q.completed_at;
          return (
            <Link
              key={q.id}
              to={`/english/${q.id}`}
              data-testid={`english-quest-card-${q.order}`}
              className={`block surface-soft px-5 py-4 transition hover:-translate-y-0.5 ${done ? "opacity-80" : ""}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0
                    ${done
                      ? "bg-[hsl(var(--amber-wash))] text-[hsl(var(--amber-deep))]"
                      : "bg-[hsl(var(--amber))] text-[hsl(var(--ink))] shadow-[0_8px_16px_-8px_hsla(36,70%,52%,0.5)]"}`}>
                    {done ? <Check className="w-5 h-5" strokeWidth={3}/> : <span className="font-bold">{q.order + 1}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="micro-label">English Quest {q.order + 1}</p>
                    <p className={`text-[17px] font-semibold truncate ${done ? "text-[hsl(var(--ink-soft))] line-through" : "text-[hsl(var(--ink))]"}`}>
                      {q.title}
                    </p>
                  </div>
                </div>
                <span className="chip">+20 XP</span>
              </div>
            </Link>
          );
        })}

        <div className="surface-violet px-5 py-5" data-testid="drum-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="micro-label text-[hsl(var(--violet-deep))]">Drums</p>
              <p className="text-[22px] text-[hsl(var(--ink))] leading-tight">
                {w.drum_done} of {w.required_drum_sessions} · <span className="text-[hsl(var(--ink-soft))] text-[16px]">{w.minutes_this_week} min this week</span>
              </p>
            </div>
            <div className="flex items-center gap-1">
              {Array.from({ length: w.required_drum_sessions }).map((_, i) => (
                <div key={i}
                  className={`w-4 h-4 rounded-full ${i < w.drum_done ? "bg-[hsl(var(--violet-deep))]" : "bg-white/70"}`}
                  data-testid={`drum-marker-${i}`}
                />
              ))}
            </div>
          </div>
          <Link to="/drum" className="btn btn-primary w-full mt-4" data-testid="start-drum-practice-btn">
            {w.drum_done >= w.required_drum_sessions ? "Practice more" : "Start practice"}
          </Link>
          <p className="text-[11px] text-center mt-2.5 text-[hsl(var(--ink-soft))] font-semibold uppercase tracking-widest">
            {w.minimum_drum_minutes}+ min counts
          </p>
        </div>
      </section>

      {/* Level + mystery reward */}
      <section className="space-y-4">
        <div className="flex items-center justify-between surface-soft px-4 py-3" data-testid="level-card">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-b from-[hsl(var(--amber))] to-[hsl(var(--amber-deep))] text-white flex items-center justify-center font-semibold text-sm shadow-[0_6px_14px_-6px_hsla(36,70%,52%,0.5)]">
              {level.level}
            </div>
            <div>
              <p className="micro-label">Level {level.level}</p>
              <div className="w-44 h-2 bg-[hsl(var(--surface-2))] rounded-full mt-1 overflow-hidden">
                <div className="h-full bg-[hsl(var(--violet))] rounded-full relative" style={{ width: `${pct}%` }}>
                  <div className="absolute inset-0 shimmer-strip" />
                </div>
              </div>
            </div>
          </div>
          <span className="text-xs font-semibold text-[hsl(var(--ink-soft))]">{level.current_xp}/{level.level_max_xp}</span>
        </div>

        {lockedReward && (
          <button
            onClick={onOpenReward}
            data-testid="mystery-reward-card"
            className="surface-soft w-full text-left px-4 py-4 flex items-center gap-4 hover:-translate-y-0.5 transition"
          >
            <img src="/assets/rewards/mystery-gift.png" alt="" className="w-14 h-14 drop-shadow-[0_12px_24px_rgba(90,60,160,0.35)]" draggable={false}/>
            <div className="flex-1 min-w-0">
              <p className="micro-label">Mystery reward</p>
              <p className="text-[17px] text-[hsl(var(--ink))] font-semibold truncate">{lockedReward.child_teaser}</p>
              <p className="text-[13px] text-[hsl(var(--ink-soft))]">
                {rewardProgressLabel(lockedReward, state)}
              </p>
            </div>
            <Lock className="w-4 h-4 text-[hsl(var(--ink-mute))]"/>
          </button>
        )}
      </section>
    </>
  );

  return (
    <div className="pb-28 pt-5">
      {/* Minimal greeting header */}
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="micro-label">{new Date().toLocaleDateString(undefined, { weekday: "long" })}</p>
          <h1 className="text-[34px] lg:text-[44px] leading-[1.05] text-[hsl(var(--ink))] mt-0.5" data-testid="home-greeting">
            Hi {state.child.name}!
          </h1>
          <p className="mt-1 text-[15px] lg:text-base text-[hsl(var(--ink-soft))]" data-testid="quests-left-count">
            {remaining === 0 && total > 0
              ? "All quests done this week"
              : `${remaining} quest${remaining === 1 ? "" : "s"} left this week`}
          </p>
        </div>
        <div className="chip chip-amber" data-testid="streak-pill">
          <Flame className="w-3.5 h-3.5" strokeWidth={2.4}/>
          <span className="font-semibold">{state.streak} wk</span>
        </div>
      </header>

      {/* Desktop two-column, mobile single-column */}
      <div className="mt-4 lg:mt-6 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] lg:gap-8 xl:gap-12 items-start">
        {/* HERO: the World (height-capped so it fits in first viewport) */}
        <section className="-mx-1 lg:mx-0 lg:sticky lg:top-6" data-testid="home-world">
          <div className="mx-auto" style={{ width: "100%", maxWidth: "min(100%, 60vh)" }}>
            <WorldScene ownedCodes={unlocked} mascotState={mascotState} mascotSize={160}/>
          </div>
        </section>

        <div className="mt-2 lg:mt-0 space-y-8">
          {RightColumn}
        </div>
      </div>
    </div>
  );
}

function rewardProgressLabel(reward, state) {
  const amt = reward.requirement_amount;
  let cur = 0;
  if (reward.requirement_type === "perfect_weeks") cur = state.totals.perfect_weeks;
  else if (reward.requirement_type === "english_quests") cur = state.totals.english_completed;
  else if (reward.requirement_type === "drum_sessions") cur = state.totals.drum_qualified;
  else if (reward.requirement_type === "xp") cur = state.xp;
  const left = Math.max(0, amt - cur);
  const labels = {
    perfect_weeks: "Perfect Weeks",
    english_quests: "English Quests",
    drum_sessions: "Drum Practices",
    xp: "XP",
  };
  return `${left} more ${labels[reward.requirement_type]} to unlock`;
}
